import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../helpers/firebase";
import userModel from "./firebase/userModel";
import unitModel from "./firebase/unitModel";
import { listTenants } from "./firebase/tenantModel";
import repairModel from "./firebase/repairModel";
import LeaseModel from "./firebase/leaseModel";
import { getExpensesByOwner } from "./firebase/expenseModel";

const VENDORS_COLLECTION = "vendors";

/**
 * Recursively turn Firestore Timestamps, Dates, and plain objects into JSON-safe values.
 */
export function deepSerializeForJsonExport(value, seen = new WeakSet()) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "number" && !Number.isFinite(value)) return null;
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().toISOString();
    } catch {
      return null;
    }
  }
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepSerializeForJsonExport(item, seen));
  }
  if (typeof value === "object") {
    if (seen.has(value)) return "[Circular]";
    seen.add(value);
    const result = {};
    for (const key of Object.keys(value)) {
      try {
        result[key] = deepSerializeForJsonExport(value[key], seen);
      } catch {
        result[key] = null;
      }
    }
    return result;
  }
  return value;
}

async function fetchAllVendorsForOwner(ownerId) {
  const q = query(
    collection(db, VENDORS_COLLECTION),
    where("ownerId", "==", ownerId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Loads landlord-owned records from Firestore for a portable JSON download.
 * Each section is best-effort: failures are listed under exportMeta.partialErrors.
 */
export async function buildPortfolioExportBundle(ownerId) {
  if (!ownerId) {
    throw new Error("Not signed in.");
  }

  const partialErrors = [];
  const fail = (section, err) => {
    partialErrors.push({
      section,
      message: err?.message || String(err),
    });
  };

  let profile = null;
  try {
    profile = await userModel.getOneById(ownerId);
  } catch (e) {
    fail("profile", e);
  }

  let doors = [];
  try {
    doors = await unitModel.getByOwner(ownerId);
  } catch (e) {
    fail("doors", e);
  }

  let tenants = [];
  try {
    const res = await listTenants();
    tenants = res?.tenants || [];
  } catch (e) {
    fail("tenants", e);
  }

  let vendorsContractors = [];
  try {
    vendorsContractors = await fetchAllVendorsForOwner(ownerId);
  } catch (e) {
    fail("vendorsContractors", e);
  }

  let maintenanceTickets = [];
  try {
    maintenanceTickets = await repairModel.getByOwner(ownerId);
  } catch (e) {
    fail("maintenanceTickets", e);
  }

  let leases = [];
  try {
    leases = await LeaseModel.getByOwner(ownerId);
  } catch (e) {
    fail("leases", e);
  }

  let expenses = [];
  try {
    const res = await getExpensesByOwner();
    if (res?.success && Array.isArray(res.expenses)) {
      expenses = res.expenses;
    } else if (res?.error) {
      fail("expenses", new Error(res.error));
    }
  } catch (e) {
    fail("expenses", e);
  }

  const bundle = {
    exportMeta: {
      generatedAt: new Date().toISOString(),
      formatVersion: 1,
      format: "LandlordOps portfolio export (JSON)",
      ownerId,
      app: "LandlordOps",
      description:
        "Plain JSON of your portfolio data from Firestore. You can import or transform this file in another tool or platform.",
      ...(partialErrors.length > 0 ? { partialErrors } : {}),
    },
    profile,
    doors,
    tenants,
    vendorsContractors,
    maintenanceTickets,
    leases,
    expenses,
  };

  return deepSerializeForJsonExport(bundle);
}

export function downloadJsonFile(data, filename) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function defaultPortfolioExportFilename() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `landlordops-portfolio-export-${y}-${m}-${day}.json`;
}
